import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useEffect, useRef } from "react"
import { useMutationWithEndpoint, useLazyQueryWithEndpoint } from "../useGraphQLClient"

/**
 * Custom hook for managing activity links
 * Provides functionality to:
 * - Fetch linkable activities (chronologically closest before/after)
 * - Create links between activities
 * - Delete activity links
 */
export function useLinkableActivities(activityId?: string) {
  // Query for linkable activities
  const [
    getLinkableActivities,
    { data: linkableData, loading: linkableLoading, error: linkableError, refetch: refetchLinkable },
  ] = useLazyQueryWithEndpoint(
    gqlTypes.GetLinkableActivitiesDocument,
    // Always fetch fresh so recently created activities appear as candidates.
    { fetchPolicy: "network-only" },
    "primary"
  )
  useEffect(() => {
    if (activityId) {
      getLinkableActivities({ variables: { activity: activityId } })
    }
  }, [activityId])

  // Mutation to create activity link
  const [createLink, { loading: createLoading, error: createError }] = useMutationWithEndpoint(
    gqlTypes.CreateActivityLinkDocument,
    { refetchQueries: ["AssetAvailabilityCalendar"], awaitRefetchQueries: false },
    "primary"
  )

  // Mutation to delete activity link
  const [deleteLink, { loading: deleteLoading, error: deleteError }] = useMutationWithEndpoint(
    gqlTypes.DeleteActivityLinkDocument,
    {
      refetchQueries: ["AssetAvailabilityCalendar"],
      awaitRefetchQueries: false,
    },
    "primary"
  )
  const linkableActivities = linkableData?.linkableActivities

  return {
    // Linkable activities data
    linkableBefore: linkableActivities?.before,
    linkableAfter: linkableActivities?.after,

    // Loading states
    loading: linkableLoading,
    createLoading,
    deleteLoading,

    // Errors
    error: linkableError,
    createError,
    deleteError,

    // Actions
    refetchLinkable,
    createLink,
    deleteLink,
  }
}

/**
 * Custom hook for finding linkable activities by dates (for activity creation)
 * Provides functionality to:
 * - Fetch linkable activities based on date range and asset
 * - Debounces requests to avoid excessive API calls
 */
export function useLinkableActivitiesByDates() {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Lazy query for linkable activities by dates
  const [fetchLinkable, { data, loading, error }] = useLazyQueryWithEndpoint(
    gqlTypes.GetLinkableActivitiesByDatesDocument,
    {
      fetchPolicy: "network-only",
    },
    "primary"
  )

  // Mutation to create activity link
  const [createLink, { loading: createLoading, error: createError }] = useMutationWithEndpoint(
    gqlTypes.CreateActivityLinkDocument,
    { refetchQueries: ["AssetAvailabilityCalendar"], awaitRefetchQueries: false },
    "primary"
  )

  // Mutation to delete activity link
  const [deleteLink, { loading: deleteLoading, error: deleteError }] = useMutationWithEndpoint(
    gqlTypes.DeleteActivityLinkDocument,
    {
      refetchQueries: ["AssetAvailabilityCalendar"],
      awaitRefetchQueries: false,
    },
    "primary"
  )

  // Debounced fetch function
  const fetchLinkableDebounced = (asset: string, start: string, end: string) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      if (asset && start && end) {
        fetchLinkable({
          variables: { asset, start, end },
        })
      }
    }, 500) // 500ms debounce
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  const linkableActivities = data?.linkableActivities

  return {
    // Linkable activities data
    linkableBefore: linkableActivities?.before,
    linkableAfter: linkableActivities?.after,

    // Loading states
    loading,
    createLoading,
    deleteLoading,

    // Errors
    error,
    createError,
    deleteError,

    // Actions
    fetchLinkable: fetchLinkableDebounced,
    createLink,
    deleteLink,
  }
}
